import { useCallback, useContext, useEffect, useRef } from 'react';

import { ConversationAssistantUIModes } from '@atlassian/conversation-assistant/types';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api/rovo-experience-api-store';

import { EDIT_PAGE_LOAD_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useToggleConversationAssistant } from '@confluence/conversation-assistant/entry-points/useToggleConversationAssistant.ts';
// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';

// Persisted per-contentId so the mini-modal auto-opens at most once per page and stays dismissed
// if the user closes it (see the localStorage usage in `useOpenRovoOnEditPageReady` below).
// Key shape: `${contentId}:rovo-auto-opened-new-page` — the dynamic contentId is the PREFIX and the
// static name is the literal TAIL. This ordering matters when browser storage controls
// is enabled: the browser-storage-controls consent layer matches keys via an ENDS_WITH registration
// on the static suffix. If a dynamic value (the contentId) were the tail instead, no static
// ENDS_WITH registration could match it, so the write would be BLOCKED and the flag never persists.
const ROVO_AUTO_OPENED_KEY_SUFFIX = ':rovo-auto-opened-new-page';

const getRovoAutoOpenedKey = (contentId: string) => `${contentId}${ROVO_AUTO_OPENED_KEY_SUFFIX}`;

const normalizeSingleQueryParam = (queryParam: string | string[] | undefined) =>
	typeof queryParam === 'string' ? queryParam : undefined;

const AUTO_OPEN_DELAY_MS = 500;

// 14 days, in seconds — after this window the Rovo mini-modal may auto-open again.
const ROVO_AUTO_OPENED_TTL_SECONDS = 14 * 24 * 60 * 60; // 1209600

type ShouldAutoOpenRovoOnEditPageReadyArgs = {
	contentId: string;
	draftShareId?: string;
	isEnabled: boolean;
	isEmptyLivePage: boolean;
};

type UseOpenRovoOnEditPageReadyArgs = {
	contentId: string;
	isEnabled: boolean;
	isEmptyLivePage: boolean;
	shouldObserveLiveEmptyPage: boolean;
	isLivePageReady: boolean;
};

type IsAutoOpenTriggerReadyArgs = {
	draftShareId?: string;
	isEditPageReady: boolean;
	isEmptyLivePage: boolean;
	shouldObserveLiveEmptyPage: boolean;
	isLivePageReady: boolean;
};

export const markRovoAutoOpened = (contentId: string): void => {
	localStorage.setItem(getRovoAutoOpenedKey(contentId), true, ROVO_AUTO_OPENED_TTL_SECONDS);
};

export const shouldAutoOpenRovoOnEditPageReady = ({
	contentId,
	draftShareId,
	isEnabled,
	isEmptyLivePage,
}: ShouldAutoOpenRovoOnEditPageReadyArgs): boolean =>
	isEnabled &&
	(!!draftShareId || isEmptyLivePage) &&
	!localStorage.getItemAsBoolean(getRovoAutoOpenedKey(contentId));

const isAutoOpenTriggerReady = ({
	draftShareId,
	isEditPageReady,
	isEmptyLivePage,
	shouldObserveLiveEmptyPage,
	isLivePageReady,
}: IsAutoOpenTriggerReadyArgs) =>
	(!!draftShareId && isEditPageReady) ||
	(shouldObserveLiveEmptyPage && isEmptyLivePage && isLivePageReady === true);

/**
 * Opens the Rovo chat mini-modal once the editor is ready, but only for newly-created pages or
 * empty live docs.
 *
 * There are two auto-open triggers:
 * - Newly-created content is detected by the presence of `draftShareId` in the URL query params at
 *   mount time. This query param is added by the create-content flow and is present in the redirect
 *   URL sent to the editor after page creation. We capture it once at mount via a ref so that the
 *   new-content trigger is immune to any subsequent URL or state changes. Draft/new pages wait for
 *   `edit-page/load` (EDIT_PAGE_LOAD_EXPERIENCE) `taskSuccess`, which fires when NCS fully connects
 *   after both VC90 and TTAI complete.
 * - Empty live docs are detected from editor empty-state. That state can be false or unknown at
 *   mount and update after live editor readiness, so the hook checks the latest value from a ref.
 *   Live docs do not emit `edit-page/load` `taskSuccess`; they use live editor readiness
 *   (`collabEditInitialized`) plus empty editor state instead.
 *
 * User preference is persisted in localStorage keyed by contentId. If the user closes the
 * mini-modal and navigates back to the same draft/live doc (even in a new browser session), we will
 * not re-open it. localStorage is preferred over sessionStorage so the dismissal persists across
 * sessions and tabs rather than only the current session. The flag expires after 14 days (see
 * ROVO_AUTO_OPENED_TTL_SECONDS), after which the mini-modal may auto-open again on the same page.
 *
 * Note: We intentionally do NOT check `getExperienceState().hasStopped` as a fast-path because
 * that state persists across navigations in the singleton ExperienceTracker. A prior page's
 * completed `edit-page/load` experience would appear as `hasStopped = true` when the editor
 * first mounts for a new page, causing the modal to open on the wrong page. Since VC90/TTAI
 * always completes well after the initial render, the subscription is always in place before
 * `taskSuccess` fires.
 */
export const useOpenRovoOnEditPageReady = ({
	contentId,
	isEnabled,
	isEmptyLivePage,
	shouldObserveLiveEmptyPage,
	isLivePageReady,
}: UseOpenRovoOnEditPageReadyArgs): void => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { getQueryParams } = useContext(RoutesContext);
	const { openConversationAssistant } = useToggleConversationAssistant();
	const { setWasAutoOpened, mode } = useRovoExperienceAPIStore();

	// Capture at mount whether this is a newly-created page (draftShareId in URL).
	// Using a ref ensures this trigger is immune to URL/state changes after initial mount.
	const { draftShareId, rovoAgentId } = getQueryParams();
	const draftShareIdRef = useRef(normalizeSingleQueryParam(draftShareId));
	const hasSelectedAgentHandoffRef = useRef(Boolean(normalizeSingleQueryParam(rovoAgentId)));

	const isEmptyLivePageRef = useRef(isEmptyLivePage);
	const isLivePageReadyRef = useRef(isLivePageReady);
	const isEditPageReadyRef = useRef(false);
	// Read the current mode via a ref inside the delayed timeout to avoid a stale closure and to
	// keep `mode` out of tryOpenRovo's deps (which would re-create the callback on every mode change).
	const modeRef = useRef(mode);
	// Guard: only open once, even across re-renders.
	const hasOpenedRef = useRef(false);
	// One-shot buffer timer for the delayed auto-open; armed once by the first eligible trigger and
	// cleared only when it fires or on unmount.
	const openTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	const tryOpenRovo = useCallback(() => {
		if (
			hasSelectedAgentHandoffRef.current ||
			hasOpenedRef.current ||
			!isAutoOpenTriggerReady({
				draftShareId: draftShareIdRef.current,
				isEditPageReady: isEditPageReadyRef.current,
				isEmptyLivePage: isEmptyLivePageRef.current,
				shouldObserveLiveEmptyPage,
				isLivePageReady: isLivePageReadyRef.current,
			}) ||
			!shouldAutoOpenRovoOnEditPageReady({
				contentId,
				draftShareId: draftShareIdRef.current,
				isEnabled,
				isEmptyLivePage: isEmptyLivePageRef.current,
			})
		) {
			return;
		}

		// One-shot buffer: the first eligible trigger arms a one-time timer so the mini-modal surface has
		// time to mount/register before we open it (avoids a mount-order race). While a timer is already
		// pending, subsequent triggers are no-ops — we never clear or reschedule, so rapid repeated
		// triggers cannot starve the open. The one-shot is consumed only when the timer actually fires.
		if (openTimerRef.current !== undefined) {
			return;
		}

		openTimerRef.current = setTimeout(() => {
			openTimerRef.current = undefined;
			hasOpenedRef.current = true;
			// Persist that we've auto-opened for this content so we don't re-open if the user dismisses
			// and navigates back to the same draft/live doc. Remembered for 14 days (see
			// ROVO_AUTO_OPENED_TTL_SECONDS); after that the mini-modal may auto-open again.
			markRovoAutoOpened(contentId);

			// Guard: modal already open — skip setWasAutoOpened/open here (the store's open path
			// would no-op and could leave a stale wasAutoOpened). NOTE: the localStorage auto-open
			// key is already written unconditionally above, so revisits won't re-trigger regardless.
			if (modeRef.current === ConversationAssistantUIModes.MINI_MODAL) {
				return;
			}

			// Mark this open as programmatic so the mini-modal suppresses the chat input's initial focus.
			setWasAutoOpened?.(true);
			openConversationAssistant('mini-modal');
		}, AUTO_OPEN_DELAY_MS);
	}, [
		contentId,
		isEnabled,
		openConversationAssistant,
		setWasAutoOpened,
		shouldObserveLiveEmptyPage,
	]);

	useEffect(() => {
		isEmptyLivePageRef.current = isEmptyLivePage;
		tryOpenRovo();
	}, [isEmptyLivePage, tryOpenRovo]);

	useEffect(() => {
		isLivePageReadyRef.current = isLivePageReady;
		tryOpenRovo();
	}, [isLivePageReady, tryOpenRovo]);

	// Keep the mode ref in sync so the delayed timeout reads the latest value without re-creating
	// tryOpenRovo on every mode change.
	useEffect(() => {
		modeRef.current = mode;
	}, [mode]);

	useEffect(() => {
		const shouldSubscribe =
			!hasSelectedAgentHandoffRef.current &&
			isEnabled &&
			!!draftShareIdRef.current &&
			!localStorage.getItemAsBoolean(getRovoAutoOpenedKey(contentId));

		if (!shouldSubscribe) {
			return;
		}

		// Draft/new pages open after edit-page/load succeeds; live docs use collab readiness instead.
		return experienceTracker.subscribe((event) => {
			if (event.name === EDIT_PAGE_LOAD_EXPERIENCE && event.action === 'taskSuccess') {
				isEditPageReadyRef.current = true;
				tryOpenRovo();
			}
		});
	}, [experienceTracker, contentId, isEnabled, tryOpenRovo]);

	useEffect(
		() => () => {
			if (openTimerRef.current !== undefined) {
				clearTimeout(openTimerRef.current);
			}
		},
		[],
	);
};
